import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Info, Lock } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { useToast } from '../components/ui/useToast';
import {
  HeuresLues,
  LigneApprenant,
  LigneSession,
  ListeVide,
  PastilleEtatMois,
  SelecteurMois,
} from '../components/formateurs/PrevisionHeures';
import {
  ColonnesHeures,
  MentionRepere,
  PastilleEtatDeclaration,
  RealiseLu,
  SelecteurMoisDeclaration,
  texteDeclaration,
} from '../components/formateurs/HeuresRealisees';
import { ErreurApi } from '../lib/api';
import { detailsRefus } from '../lib/erreurs-api';
import {
  useDeclarerHeures,
  useEnregistrerMaPrevision,
  useMaDeclaration,
  useMaPrevision,
  type SaisiePrevision,
  type VueDeclarationFormateur,
  type VuePrevisionFormateur,
} from '../lib/useEspaceFormateur';

type OngletHeures = 'previsionnel' | 'realise';

/**
 * « Mes heures » du formateur (phase 4) — un écran, deux onglets :
 *
 * - **Prévisionnel** (TF-419) : ses heures du mois à venir — et des deux
 *   suivants —, par session ou par apprenant, figées au 1er du mois ;
 * - **Heures réalisées** (TF-420) : la déclaration du mois fini, en face du
 *   prévisionnel, avec l'écart et le repère émargé de ses séances. Elle
 *   s'ouvre le dernier jour du mois et se corrige jusqu'à la fin du mois
 *   suivant ; la relance y mène (`?onglet=realise&mois=AAAA-MM`).
 *
 * Mois, périmètre, cumul, écart et repère sont tenus par l'API.
 */
export function FormHeuresPage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const onglet: OngletHeures = parametres.get('onglet') === 'realise' ? 'realise' : 'previsionnel';
  const [moisPrevision, setMoisPrevision] = useState<string | null>(null);
  const [moisDeclaration, setMoisDeclaration] = useState<string | null>(() =>
    onglet === 'realise' ? parametres.get('mois') : null,
  );
  const prevision = useMaPrevision(moisPrevision);
  const declaration = useMaDeclaration(moisDeclaration);
  const aDeclarer =
    declaration.data?.moisProposes.filter((propose) => propose.etat === 'A_DECLARER' || propose.etat === 'EN_RETARD')
      .length ?? 0;

  const changerOnglet = (cle: OngletHeures): void => {
    const suivants = new URLSearchParams(parametres);
    if (cle === 'realise') suivants.set('onglet', 'realise');
    else suivants.delete('onglet');
    suivants.delete('mois');
    setParametres(suivants, { replace: true });
  };

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Mes heures"
        sousTitre="Espace formateur · prévisionnel du mois à venir et déclaration des heures réalisées"
      />
      <Onglets
        etiquette="Mes heures"
        onglets={[
          { cle: 'previsionnel', libelle: 'Prévisionnel' },
          { cle: 'realise', libelle: 'Heures réalisées', compte: aDeclarer },
        ]}
        actif={onglet}
        onChanger={changerOnglet}
        idPanneau="panneau-heures"
        actions={
          onglet === 'previsionnel'
            ? prevision.data?.aFiche && (
                <SelecteurMois
                  id="mois-prevision"
                  mois={prevision.data.mois}
                  proposes={prevision.data.moisProposes}
                  onChange={setMoisPrevision}
                />
              )
            : declaration.data?.aFiche && (
                <SelecteurMoisDeclaration
                  id="mois-declaration"
                  mois={declaration.data.mois}
                  proposes={declaration.data.moisProposes}
                  onChange={setMoisDeclaration}
                />
              )
        }
      />

      <PanneauOnglet id="panneau-heures" actif={onglet} className="space-y-6">
        {onglet === 'previsionnel' ? (
          <>
            {prevision.isLoading && <Chargement />}
            {prevision.isError && (
              <MessageErreur
                message={
                  prevision.error instanceof ErreurApi
                    ? prevision.error.message
                    : 'Les heures prévisionnelles n’ont pas pu être lues.'
                }
              />
            )}
            {prevision.data && !prevision.data.aFiche && (
              <MessageErreur message={prevision.data.refus ?? 'Votre fiche formateur manque.'} />
            )}
            {prevision.data?.aFiche && (
              // Une saisie enregistrée revient de l'API : le formulaire repart de ce qu'elle a retenu.
              <Prevision key={`${prevision.data.mois}-${empreinte(prevision.data)}`} vue={prevision.data} />
            )}
          </>
        ) : (
          <>
            {declaration.isLoading && <Chargement />}
            {declaration.isError && (
              <MessageErreur
                message={
                  declaration.error instanceof ErreurApi
                    ? declaration.error.message
                    : 'Les heures réalisées n’ont pas pu être lues.'
                }
              />
            )}
            {declaration.data && !declaration.data.aFiche && (
              <MessageErreur message={declaration.data.refus ?? 'Votre fiche formateur manque.'} />
            )}
            {declaration.data?.aFiche && (
              <Declaration
                key={`${declaration.data.mois}-${empreinteDeclaration(declaration.data)}`}
                vue={declaration.data}
              />
            )}
          </>
        )}
      </PanneauOnglet>
    </div>
  );
}

/** Ce que le formulaire a reçu : il se réinitialise quand cela change. */
function empreinte(vue: VuePrevisionFormateur): string {
  return [...vue.sessions, ...vue.apprenants]
    .map((ligne) => ('sessionId' in ligne ? ligne.sessionId : ligne.apprenantId) + ':' + (ligne.heures?.minutes ?? ''))
    .join('|');
}

/** « de novembre 2026 », « d’octobre 2026 ». */
function deMois(libelle: string): string {
  return /^[aeiouâéèêîôû]/i.test(libelle) ? `d’${libelle}` : `de ${libelle}`;
}

function valeurInitiale(heures: { valeur: number } | null): string {
  return heures ? String(heures.valeur).replace('.', ',') : '';
}

/** « 35 », « 35,5 », « 35.5 » — vide : rien de prévu. `undefined` : saisie illisible. */
function lireHeures(saisie: string): number | null | undefined {
  const texte = saisie.trim().replace(/\s/g, '').replace(',', '.');
  if (texte === '') return null;
  if (!/^\d+(\.\d+)?$/.test(texte)) return undefined;
  return Number(texte);
}

function messageRefus(erreur: unknown): string {
  const detail = detailsRefus(erreur)[0];
  if (detail) return detail.message;
  return erreur instanceof ErreurApi ? erreur.message : 'Le prévisionnel n’a pas pu être enregistré.';
}

const CLASSE_CASE =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-right text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function CaseHeures({
  id,
  libelle,
  valeur,
  onChange,
}: {
  id: string;
  libelle: string;
  valeur: string;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="sr-only">
        {libelle}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder="—"
        value={valeur}
        onChange={(evenement) => onChange(evenement.target.value)}
        className={CLASSE_CASE}
      />
      <span className="text-sm text-codialis-gray" aria-hidden>
        h
      </span>
    </div>
  );
}

function Prevision({ vue }: { vue: VuePrevisionFormateur }): JSX.Element {
  const toast = useToast();
  const enregistrer = useEnregistrerMaPrevision();
  const ouvert = vue.etat === 'OUVERT';
  const [sessions, setSessions] = useState<Record<string, string>>(() =>
    Object.fromEntries(vue.sessions.map((ligne) => [ligne.sessionId, valeurInitiale(ligne.heures)])),
  );
  const [apprenants, setApprenants] = useState<Record<string, string>>(() =>
    Object.fromEntries(vue.apprenants.map((ligne) => [ligne.apprenantId, valeurInitiale(ligne.heures)])),
  );
  const [erreur, setErreur] = useState<string | null>(null);

  const modifie =
    vue.sessions.some((ligne) => (sessions[ligne.sessionId] ?? '') !== valeurInitiale(ligne.heures)) ||
    vue.apprenants.some((ligne) => (apprenants[ligne.apprenantId] ?? '') !== valeurInitiale(ligne.heures));
  const prevues = (lignes: readonly { heures: unknown }[]) => lignes.filter((ligne) => ligne.heures !== null).length;

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const saisie: SaisiePrevision = { sessions: [], apprenants: [] };
    for (const ligne of vue.sessions) {
      if (ligne.horsPerimetre) continue;
      const heures = lireHeures(sessions[ligne.sessionId] ?? '');
      if (heures === undefined) {
        setErreur(`Heures illisibles pour la session « ${ligne.libelle} » : saisissez par exemple 35 ou 35,5.`);
        return;
      }
      if (heures !== null) saisie.sessions.push({ sessionId: ligne.sessionId, heures });
    }
    for (const ligne of vue.apprenants) {
      if (ligne.horsPerimetre) continue;
      const heures = lireHeures(apprenants[ligne.apprenantId] ?? '');
      if (heures === undefined) {
        setErreur(`Heures illisibles pour ${ligne.apprenant} : saisissez par exemple 35 ou 35,5.`);
        return;
      }
      if (heures !== null) saisie.apprenants.push({ apprenantId: ligne.apprenantId, heures });
    }
    setErreur(null);
    enregistrer.mutate(
      { mois: vue.mois, saisie },
      {
        onSuccess: () => toast(`Prévisionnel ${deMois(vue.libelle)} enregistré.`),
        onError: (refus) => setErreur(messageRefus(refus)),
      },
    );
  };

  return (
    <form onSubmit={soumettre} className="space-y-6" aria-label={`Prévisionnel ${deMois(vue.libelle)}`}>
      <p
        className={`flex gap-2 rounded-xl px-3 py-2.5 text-xs ${
          ouvert ? 'bg-codialis-sand text-codialis-gray' : 'bg-amber-50 text-amber-900'
        }`}
      >
        {ouvert ? (
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
        ) : (
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        )}
        <span className="min-w-0">
          {ouvert
            ? `Saisie ouverte jusqu’au dernier jour du mois qui précède. Le 1er ${vue.libelle}, le prévisionnel est figé : il servira de référence à la déclaration des heures réalisées. Une ligne de session vaut pour chacun de ses apprenants ; un apprenant se prévoit sur sa ligne ou sur celle de sa session, pas les deux. Videz une case pour ne rien prévoir.`
            : vue.refus}
        </span>
      </p>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="col-span-2 min-w-0 sm:col-span-1">
          <Compteur libelle={`Total prévu · ${vue.libelle}`} valeur={vue.total.texte} ton="mint" />
        </div>
        <Compteur
          libelle="Sessions prévues"
          valeur={`${prevues(vue.sessions)} / ${vue.sessions.length}`}
          detail={ouvert ? 'où vous animez ou suivez un apprenant' : undefined}
        />
        <Compteur
          libelle="Apprenants prévus"
          valeur={`${prevues(vue.apprenants)} / ${vue.apprenants.length}`}
          detail={ouvert ? 'parcours OF dont vous êtes le référent' : undefined}
        />
      </div>

      <Carte titre="Mes sessions" actions={<PastilleEtatMois etat={vue.etat} />} sansBordure>
        {vue.sessions.length === 0 ? (
          <ListeVide>
            {ouvert
              ? `Aucune session en cours en ${vue.libelle} où vous animez une séance ou suivez un apprenant. Une session paraît ici dès que vous y avez une séance ou un apprenant référent.`
              : `Aucune session prévue pour ${vue.libelle}.`}
          </ListeVide>
        ) : (
          <ul className="divide-y divide-gray-100">
            {vue.sessions.map((ligne) => (
              <LigneSession
                key={ligne.sessionId}
                ligne={ligne}
                heures={
                  ouvert && !ligne.horsPerimetre ? (
                    <CaseHeures
                      id={`heures-session-${ligne.sessionId}`}
                      libelle={`Heures prévues pour la session ${ligne.libelle}`}
                      valeur={sessions[ligne.sessionId] ?? ''}
                      onChange={(valeur) => setSessions((actuel) => ({ ...actuel, [ligne.sessionId]: valeur }))}
                    />
                  ) : (
                    <HeuresLues texte={ligne.heures?.texte ?? null} />
                  )
                }
              />
            ))}
          </ul>
        )}
      </Carte>

      <Carte titre="Mes apprenants" actions={<PastilleEtatMois etat={vue.etat} />} sansBordure>
        {vue.apprenants.length === 0 ? (
          <ListeVide>
            {ouvert
              ? `Aucun apprenant d’un parcours OF dont vous êtes le référent n’est en formation en ${vue.libelle}.`
              : `Aucun apprenant prévu pour ${vue.libelle}.`}
          </ListeVide>
        ) : (
          <ul className="divide-y divide-gray-100">
            {vue.apprenants.map((ligne) => (
              <LigneApprenant
                key={ligne.apprenantId}
                ligne={ligne}
                heures={
                  ouvert && !ligne.horsPerimetre ? (
                    <CaseHeures
                      id={`heures-apprenant-${ligne.apprenantId}`}
                      libelle={`Heures prévues pour ${ligne.apprenant}`}
                      valeur={apprenants[ligne.apprenantId] ?? ''}
                      onChange={(valeur) => setApprenants((actuel) => ({ ...actuel, [ligne.apprenantId]: valeur }))}
                    />
                  ) : (
                    <HeuresLues texte={ligne.heures?.texte ?? null} />
                  )
                }
              />
            ))}
          </ul>
        )}
      </Carte>

      {ouvert && (vue.sessions.length > 0 || vue.apprenants.length > 0) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          {erreur && (
            <p role="alert" className="min-w-0 text-sm text-red-700 sm:mr-auto">
              {erreur}
            </p>
          )}
          {modifie && !erreur && <Pastille texte="Modifications non enregistrées" ton="warning" />}
          <BoutonPrincipal type="submit" disabled={enregistrer.isPending || !modifie}>
            {enregistrer.isPending ? 'Enregistrement…' : `Enregistrer le prévisionnel ${deMois(vue.libelle)}`}
          </BoutonPrincipal>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Heures réalisées (TF-420)
// ---------------------------------------------------------------------------

function empreinteDeclaration(vue: VueDeclarationFormateur): string {
  return [
    vue.declaration?.corrigeeLe ?? vue.declaration?.declareeLe ?? '',
    ...[...vue.sessions, ...vue.apprenants].map(
      (ligne) => ('sessionId' in ligne ? ligne.sessionId : ligne.apprenantId) + ':' + (ligne.realise?.minutes ?? ''),
    ),
  ].join('|');
}

const OUVERTE: ReadonlySet<VueDeclarationFormateur['etat']> = new Set(['A_DECLARER', 'EN_RETARD', 'DECLAREE']);

function Declaration({ vue }: { vue: VueDeclarationFormateur }): JSX.Element {
  const toast = useToast();
  const declarer = useDeclarerHeures();
  const ouverte = OUVERTE.has(vue.etat) && vue.refus === null;
  const [sessions, setSessions] = useState<Record<string, string>>(() =>
    Object.fromEntries(vue.sessions.map((ligne) => [ligne.sessionId, valeurInitiale(ligne.realise)])),
  );
  const [apprenants, setApprenants] = useState<Record<string, string>>(() =>
    Object.fromEntries(vue.apprenants.map((ligne) => [ligne.apprenantId, valeurInitiale(ligne.realise)])),
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmerZero, setConfirmerZero] = useState<SaisiePrevision | null>(null);

  const modifie =
    vue.sessions.some((ligne) => (sessions[ligne.sessionId] ?? '') !== valeurInitiale(ligne.realise)) ||
    vue.apprenants.some((ligne) => (apprenants[ligne.apprenantId] ?? '') !== valeurInitiale(ligne.realise));
  const premiere = vue.declaration === null;
  const declaree = texteDeclaration(vue.declaration);

  const envoyer = (saisie: SaisiePrevision): void => {
    declarer.mutate(
      { mois: vue.mois, saisie },
      {
        onSuccess: () => {
          setConfirmerZero(null);
          toast(premiere ? `Heures ${deMois(vue.libelle)} déclarées.` : `Déclaration ${deMois(vue.libelle)} corrigée.`);
        },
        onError: (refus) => {
          setConfirmerZero(null);
          setErreur(messageRefus(refus));
        },
      },
    );
  };

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const saisie: SaisiePrevision = { sessions: [], apprenants: [] };
    for (const ligne of vue.sessions) {
      if (ligne.horsPerimetre) continue;
      const heures = lireHeures(sessions[ligne.sessionId] ?? '');
      if (heures === undefined) {
        setErreur(`Heures illisibles pour la session « ${ligne.libelle} » : saisissez par exemple 35, 35,5 ou 0.`);
        return;
      }
      if (heures !== null) saisie.sessions.push({ sessionId: ligne.sessionId, heures });
    }
    for (const ligne of vue.apprenants) {
      if (ligne.horsPerimetre) continue;
      const heures = lireHeures(apprenants[ligne.apprenantId] ?? '');
      if (heures === undefined) {
        setErreur(`Heures illisibles pour ${ligne.apprenant} : saisissez par exemple 35, 35,5 ou 0.`);
        return;
      }
      if (heures !== null) saisie.apprenants.push({ apprenantId: ligne.apprenantId, heures });
    }
    setErreur(null);
    // Rien de saisi : une déclaration à zéro heure se confirme, elle ne part pas d'un clic distrait.
    if (saisie.sessions.length === 0 && saisie.apprenants.length === 0) {
      setConfirmerZero(saisie);
      return;
    }
    envoyer(saisie);
  };

  const enRetard = vue.etat === 'EN_RETARD';
  const bandeau = vue.refus
    ? { classe: 'bg-amber-50 text-amber-900', icone: <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> }
    : enRetard
      ? { classe: 'bg-red-50 text-red-800', icone: <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> }
      : {
          classe: 'bg-codialis-sand text-codialis-gray',
          icone: <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />,
        };
  const caseOuverte = (horsPerimetre: boolean): boolean => ouverte && !horsPerimetre;

  return (
    <form onSubmit={soumettre} className="space-y-6" aria-label={`Heures réalisées ${deMois(vue.libelle)}`}>
      <div className={`flex gap-2 rounded-xl px-3 py-2.5 text-xs ${bandeau.classe}`}>
        {bandeau.icone}
        <div className="min-w-0 space-y-1">
          <p>{vue.avis}</p>
          {ouverte && (
            <p>
              Une ligne de session vaut pour chacun de ses apprenants ; un apprenant se déclare sur sa ligne ou sur
              celle de sa session, pas les deux. Une case vide ne déclare rien : elle compte pour 0 h dans l’écart.
              Le repère émargé n’est qu’un appui : c’est votre déclaration qui fait foi.
            </p>
          )}
          {declaree && <p className="font-semibold">{declaree}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Compteur libelle={`Prévu · ${vue.libelle}`} valeur={vue.total.prevu.texte} />
        <Compteur libelle="Réalisé déclaré" valeur={vue.total.realise?.texte ?? '—'} ton="mint" />
        <Compteur
          libelle="Écart"
          valeur={vue.total.ecart?.texte ?? '—'}
          ton={vue.total.ecart?.sens === 'MOINS' ? 'warm' : vue.total.ecart?.sens === 'PLUS' ? 'blue' : 'navy'}
          detail={vue.total.ecart ? 'réalisé − prévu' : 'après la déclaration'}
        />
        <Compteur
          libelle="Émargé sur vos séances"
          valeur={vue.total.repere.heures}
          detail={
            vue.total.repere.feuillesOuvertes > 0
              ? `${vue.total.repere.feuillesOuvertes} demi-journée${vue.total.repere.feuillesOuvertes > 1 ? 's' : ''} non clôturée${vue.total.repere.feuillesOuvertes > 1 ? 's' : ''}`
              : 'feuilles clôturées'
          }
        />
      </div>

      <Carte
        titre="Mes sessions"
        actions={<PastilleEtatDeclaration etat={vue.etat} libelle={vue.etatLibelle} />}
        sansBordure
      >
        {vue.sessions.length === 0 ? (
          <ListeVide>
            {`Aucune session en ${vue.libelle} où vous animez une séance, suivez un apprenant ou aviez prévu des heures.`}
          </ListeVide>
        ) : (
          <ul className="divide-y divide-gray-100">
            {vue.sessions.map((ligne) => (
              <LigneSession
                key={ligne.sessionId}
                ligne={ligne}
                complement={<MentionRepere repere={ligne.repere} />}
                heures={
                  <ColonnesHeures
                    prevu={ligne.prevu}
                    ecart={ligne.ecart}
                    realise={
                      caseOuverte(ligne.horsPerimetre) ? (
                        <CaseHeures
                          id={`realise-session-${ligne.sessionId}`}
                          libelle={`Heures réalisées pour la session ${ligne.libelle}`}
                          valeur={sessions[ligne.sessionId] ?? ''}
                          onChange={(valeur) => setSessions((actuel) => ({ ...actuel, [ligne.sessionId]: valeur }))}
                        />
                      ) : (
                        <RealiseLu texte={ligne.realise?.texte ?? null} />
                      )
                    }
                  />
                }
              />
            ))}
          </ul>
        )}
      </Carte>

      <Carte
        titre="Mes apprenants"
        actions={<PastilleEtatDeclaration etat={vue.etat} libelle={vue.etatLibelle} />}
        sansBordure
      >
        {vue.apprenants.length === 0 ? (
          <ListeVide>
            {`Aucun apprenant d’un parcours OF dont vous êtes le référent en ${vue.libelle}, ni d’apprenant prévu.`}
          </ListeVide>
        ) : (
          <ul className="divide-y divide-gray-100">
            {vue.apprenants.map((ligne) => (
              <LigneApprenant
                key={ligne.apprenantId}
                ligne={ligne}
                geste="declaration"
                complement={<MentionRepere repere={ligne.repere} />}
                heures={
                  <ColonnesHeures
                    prevu={ligne.prevu}
                    ecart={ligne.ecart}
                    realise={
                      caseOuverte(ligne.horsPerimetre) ? (
                        <CaseHeures
                          id={`realise-apprenant-${ligne.apprenantId}`}
                          libelle={`Heures réalisées pour ${ligne.apprenant}`}
                          valeur={apprenants[ligne.apprenantId] ?? ''}
                          onChange={(valeur) =>
                            setApprenants((actuel) => ({ ...actuel, [ligne.apprenantId]: valeur }))
                          }
                        />
                      ) : (
                        <RealiseLu texte={ligne.realise?.texte ?? null} />
                      )
                    }
                  />
                }
              />
            ))}
          </ul>
        )}
      </Carte>

      {ouverte && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          {erreur && (
            <p role="alert" className="min-w-0 text-sm text-red-700 sm:mr-auto">
              {erreur}
            </p>
          )}
          {modifie && !erreur && <Pastille texte="Modifications non enregistrées" ton="warning" />}
          <BoutonPrincipal type="submit" disabled={declarer.isPending || (!premiere && !modifie)}>
            {declarer.isPending
              ? 'Enregistrement…'
              : premiere
                ? `Déclarer les heures ${deMois(vue.libelle)}`
                : 'Enregistrer la correction'}
          </BoutonPrincipal>
        </div>
      )}

      <Modale
        titre="Aucune heure ce mois-ci ?"
        ouverte={confirmerZero !== null}
        onFermer={() => setConfirmerZero(null)}
        largeur="max-w-lg"
      >
        <div className="space-y-5 p-4 sm:p-6">
          <p className="flex items-start gap-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {`Aucune case n’est remplie : vous déclarez n’avoir effectué aucune heure en ${vue.libelle}.`}
          </p>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <BoutonSecondaire onClick={() => setConfirmerZero(null)} disabled={declarer.isPending}>
              Revenir à la saisie
            </BoutonSecondaire>
            <BoutonPrincipal
              onClick={() => confirmerZero && envoyer(confirmerZero)}
              disabled={declarer.isPending}
            >
              {declarer.isPending ? 'Enregistrement…' : 'Déclarer 0 h'}
            </BoutonPrincipal>
          </div>
        </div>
      </Modale>
    </form>
  );
}
